/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */
// import Adapter from '@/services/adapter'
import useNodeClient from '@/composables/services/useNodeClient';
const Adapter = useNodeClient();
import initializeBox from '@/box/initializeBox';
import BoxError from '@/box/BoxError';
import { useMainStore } from '@/stores/store';
import { useConfigurationStore } from "@/stores/configurationModule";
import { useCartStore } from "@/stores/cart";
import { queryToObject } from "@/utils/common";
import { scaleToPPI } from '@/utils/configuration';
import slugParser from '@/box/utils/slug-parser';

// import WPClient from '@/services/wpClient';
import useWPClient from '@/composables/services/useWPClient';
const WPClient = useWPClient();
function validateQueryObject(query) {
  // List of required fields excluding 'id'
  const requiredFields = ['slug' /*'varId'*/];

  // Check if only 'id' is present
  // @NOTE: Besides id there can be other params but not slug, varId, etc.
  if ('id' in query && Object.keys(query).length === 1) {
    return true; // Only 'id' is present and nothing else
  } else if (!('id' in query)) {
    // 'id' is not present, check for all other fields
    return requiredFields.some(field => field in query);
  }
  return false; // If 'id' is present with other fields, or not all required fields are present
}
function checkQueryForInsidePrinting(query) {
  return query.insidePrinting === 'true';
}
function checkQueryForQuantity(query) {
  const configurationStore = useConfigurationStore();
  const {
    boxQuantity
  } = storeToRefs(configurationStore);
  return query.quantity || boxQuantity.value;
}

/**
 * @param {Object} boxParams
 * @returns {Boolean}
 */
function isValidBoxParams(boxParams) {
  const {
    box: {
      boxWidth,
      boxLength,
      boxDepth,
      boxType
    }
  } = boxParams;
  const AVAILABLE_BOX_TYPES = ['Mailer', 'Shipping', 'Literature', 'Pizza'];
  if ([boxWidth, boxLength, boxDepth].some(isNaN) || !AVAILABLE_BOX_TYPES.includes(boxType)) {
    return false;
  }
  return true;
}

/**
 * useBoxQueryParameters
 * 1. turn query parameters into object
 * 2. validate query object -> must have id or slug and varId
 * 3. if id is present configuration Node API
 * 4. if no id is present, fetch product data from WP *
 *
 * @returns {Promise<void>}
 * @throws {BoxError}
 */
export default async function useBoxQueryParameters(query) {
  const mainStore = useMainStore();
  const {
    appMode
  } = storeToRefs(mainStore);
  const isQueryValid = validateQueryObject(query);
  if (!isQueryValid) {
    console.warn("QUERY IS NOT VALID", query);
    throw new BoxError('Invalid query parameters');
  }
  const configurationStore = useConfigurationStore();
  const {
    setProduct,
    setConfiguration,
    setHasInsidePrinting,
    setInsideMaterialColor,
    setOutsideMaterialColor,
    setBox,
    setBoxQuantity
  } = configurationStore;
  const {
    pixelsPerInch,
    bleed,
    materialThickness
  } = storeToRefs(configurationStore);
  let payload = {};
  let configurationObject = {};
  let boxParams = {};

  // @NOTE: Limit max and min dimension values
  // Handle new box with query params
  if (!query.id) {
    try {
      let product = null;
      if (query.slug) {
        product = await WPClient.getProductBySlug(query.slug);
      } else if (!query.slug && query.varId) {
        product = await WPClient.getProductById(query.varId);
      } else {
        console.warn("NO SLUG OR VAR ID");
      }

      /**
       * @NOTE: GUARD MUST BE REIMPLEMENTED WITH CUSTOM DIMENSIONS
       * NOW, CUSTOM DIMENSIONS ARE NOT SUPPORTED
       *
       * THIS ALSO HANDLES IF WP REST IS DOWN - REDO
       */
      if (!product) {
        throw new BoxError('Could not fetch product data');
      }
      setProduct(product);
      let insidePrinting = checkQueryForInsidePrinting(query);
      const quantity = checkQueryForQuantity(query);
      setBoxQuantity(quantity);
      const variationId = product.variation_id;
      const variationSlug = product.variation_slug;
      boxParams = slugParser(variationSlug);
      const {
        typeCode
      } = boxParams;

      /**
       * @NOTE: Better way to handle this would be to have info from WP
       * whether the product has inside printing or not
       */
      // if (type === 'Shipping' && appMode.value === 'production') {
      //   insidePrinting = false
      // }

      // Limit inside printing | @TODO: Get this list from WP
      const AVAILABLE_FOR_INSIDE_PRINT = ['mbti', 'mbtl'];
      if (!AVAILABLE_FOR_INSIDE_PRINT.includes(typeCode)) {
        insidePrinting = false;
      }
      setHasInsidePrinting(insidePrinting);
      configurationObject = {
        meta: {
          variationId: variationId,
          slug: variationSlug
        },
        box: {
          boxType: boxParams.type,
          boxWidth: boxParams.width,
          boxLength: boxParams.length,
          boxDepth: boxParams.depth
        },
        canvases: []
      };
      payload = configurationObject;
    } catch (error) {
      console.warn("QUERY IS NOT VALID", query);
      throw new BoxError('Invalid box parameters');
    }
  }

  // Handle configuration id
  if (query.id) {
    // const configuration = await fetchConfigurationById(query.id);
    const configuration = await Adapter.getConfigurationById(query.id);
    if (!configuration) {
      throw new BoxError('Configuration not found');
    }
    configurationObject = scaleToPPI(configuration);
    const variationId = configuration.meta?.variationId;
    const product = await WPClient.getProductById(variationId);
    if (!product) {
      console.error('Could not fetch product data');
      // throw new BoxError('Could not fetch product data')
    }
    setProduct(product);
    boxParams = slugParser(configuration.meta.slug);
    setHasInsidePrinting(configuration?.canvases?.length > 1);
    payload = configuration;
  }

  // @TODO: refactor validation
  // if (!isValidBoxParams(payload)) {
  //   throw new BoxError('Invalid box parameters')
  // }

  const {
    insideMaterial,
    outsideMaterial
  } = boxParams;
  setInsideMaterialColor(insideMaterial);
  setOutsideMaterialColor(outsideMaterial);
  const {
    box: {
      boxDepth,
      boxType
    }
  } = payload;
  let {
    box: {
      boxWidth,
      boxLength
    }
  } = payload;

  // @TODO: FIX!
  // @TEMP: flip box dimensions od shipping box in case width > length DOESN'T WORK!!!
  if (boxType === 'Shipping' && Number(boxWidth) > Number(boxLength)) {
    [boxWidth, boxLength] = [boxLength, boxWidth];
  }
  const params = {
    width: Number(boxWidth),
    length: Number(boxLength),
    depth: Number(boxDepth),
    materialThickness: materialThickness.value,
    bleed: bleed.value,
    pixelsPerInch: pixelsPerInch.value,
    outsideMaterial: outsideMaterial,
    insideMaterial: insideMaterial
  };

  // Temporary class initialization, in final version it should be done through box factory
  const type = boxType;
  const box = initializeBox(type, params);
  setBox(box);

  // @TODO: refactor validation
  if (!isValidBoxParams(payload)) {
    throw new BoxError('Invalid box parameters');
  }
  setConfiguration(configurationObject);

  // @NOTE Rethink this
  const cartItems = await WPClient.getCartItems();
  const cartStore = useCartStore();
  const {
    setCart,
    setProductPricesObject
  } = cartStore;
  if (cartItems && cartItems.length !== 0) {
    setCart(cartItems);
    const cartItem = cartItems.find(item => item?.meta_data?.configuration_id === query.id);
    if (cartItem) {
      setBoxQuantity(cartItem?.quantity);
    }
  }
  const pricingData = await WPClient.getPricingForProduct(configurationObject.meta.slug);
  setProductPricesObject(pricingData);
}